<template>
    <div>
        <Loader v-if="loading" />
        <div v-else class="container-fluid mt-45">
            <div class="row">
                <div class="col-md-6">
                    <Breadcrumbs />
                </div>

                <div class="col-md-6 text-right">
                    <button @click="cancelTest()" class="btn btn-danger btn-cancel-test btn-block-mobile btn-lg lg-shadow mt-4 mt-md-0">Cancel test</button>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5>What is the reason for the test?<span class="text-danger">*</span></h5>

                            <div class="row mt-4">
                                <div class="col-lg-3">
                                    <select class="custom-select" :class="{'border border-danger' : errors && errors.reason}" @change="reasonChanged()" v-model="reasonComputed">
                                        <!--<option disabled value="null">Please select an option</option>-->
                                        <option :value="TestIssueReason.GD">Gestational Diabetes Mellitus</option>
                                        <option :value="TestIssueReason.CF">Cystic Fibrosis</option>
                                        <option :value="TestIssueReason.TYPE1">Type 1</option>
                                        <option :value="TestIssueReason.TYPE2">Type 2</option>
                                        <option :value="TestIssueReason.RESEARCH_STUDY">Research Study</option>
                                    </select>
                                </div>
                            </div>

                            <small v-if="errors && errors.reason" class="text-danger mt-1">
                                {{ errors.reason[0] }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="reason != null && reason !== TestIssueReason.GD" class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5>When does the test need to be taken?<span class="text-danger">*</span></h5>
                            <div class="row mt-4">
                                <div class="col-lg-3">
                                    <b-form-datepicker id="test-datepicker" :min="testMin" :max="testMax" v-model="testDateComputed" class="mb-2"></b-form-datepicker>
                                </div>
                            </div>
                            <small v-if="errors && errors.testDate" class="text-danger mt-1">
                                {{ errors.testDate[0] }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="reason != null && reason === TestIssueReason.GD" class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5>What is the baby's expected delivery date? (EDD)<span class="text-danger">*</span></h5>
                            <div class="row mt-4">
                                <div class="col-lg-3">
                                    <b-form-datepicker @input="getPatientTestDates()" :min="eddMin" :max="eddMax" v-model="eddDateComputed" class="mb-2"></b-form-datepicker>
                                </div>
                            </div>
                            <small v-if="errors && errors.eddDate" class="text-danger mt-1">
                                {{ errors.eddDate[0] }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="reason != null && reason === TestIssueReason.GD && testDatesResponse.length" class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5 class="mb-0">When would you like the patient to take a test?<span class="text-danger">*</span></h5>
                            <p class="text-sm">Dates are calculated using 40 week gestation</p>
                            <div v-for="(testDate, index) in testDatesResponse" :key="index">
                                <PatientDatePicker v-if="!isDateInPast(testDate.date)" :min="testMin" :max="testMax" :errors="errors" :test="testDate" :key="testDate.name" />
                            </div>

                            <small v-if="errors && errors.testDates" class="text-danger d-block mt-2">
                                {{ errors.testDates[0] }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="reason != null && canShowRiskFactors" class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5 class="mb-4">Risk factors</h5>

                            <div v-if="reason === TestIssueReason.GD">
                                <ul class="list-unstyled" style="column-count: 3">
                                <li v-for="(reason, index) in myOrganisationSettings.testReasons.gdReasons" :key="index">
                                    <div class="custom-tick">
                                        <div class="form-check mt-0 mb-3">
                                            <input @change="toggleTestReason(reason)" class="form-check-input" type="checkbox" :checked="isTestReasonSelected(reason)">
                                            <label class="form-check-label">{{ getReasonLabel(reason) }}</label>
                                        </div>
                                    </div>
                                </li>
                                </ul>
                            </div>

                            <div v-if="reason === TestIssueReason.CF">
                                <ul class="list-unstyled" style="column-count: 3">
                                <li v-for="(reason, index) in myOrganisationSettings.testReasons.cfReasons" :key="index">
                                    <div class="custom-tick">
                                        <div class="form-check mt-0 mb-3">
                                            <input @change="toggleTestReason(reason)" class="form-check-input" type="checkbox" :checked="isTestReasonSelected(reason)">
                                            <label class="form-check-label">
                                                {{ getReasonLabel(reason) }}
                                            </label>
                                        </div>
                                    </div>
                                </li>
                                </ul>
                            </div>

                            <div v-if="reason === TestIssueReason.TYPE1">
                                <ul class="list-unstyled" style="column-count: 3">
                                <li v-for="(reason, index) in myOrganisationSettings.testReasons.t1Reasons" :key="index">
                                    <div class="custom-tick">
                                        <div class="form-check mt-0 mb-3">
                                            <input @change="toggleTestReason(reason)" class="form-check-input" type="checkbox" :checked="isTestReasonSelected(reason)">
                                            <label class="form-check-label">{{ getReasonLabel(reason) }}</label>
                                        </div>
                                    </div>
                                </li>
                                </ul>
                            </div>

                            <div v-if="reason === TestIssueReason.TYPE2">
                                <ul class="list-unstyled" style="column-count: 3">
                                <li v-for="(reason, index) in myOrganisationSettings.testReasons.t2Reasons" :key="index">
                                    <div class="custom-tick">
                                        <div class="form-check mt-0 mb-3">
                                            <input @change="toggleTestReason(reason)" class="form-check-input" type="checkbox" :checked="isTestReasonSelected(reason)">
                                            <label class="form-check-label">{{ getReasonLabel(reason) }}</label>
                                        </div>
                                    </div>
                                </li>
                                </ul>
                            </div>

                            <div v-if="reason === TestIssueReason.RESEARCH_STUDY">
                                <ul class="list-unstyled" style="column-count: 3">
                                <li v-for="(reason, index) in myOrganisationSettings.testReasons.researchStudyReasons" :key="index">
                                    <div class="custom-tick">
                                        <div class="form-check mt-0 mb-3">
                                            <input @change="toggleTestReason(reason)" class="form-check-input" type="checkbox" :checked="isTestReasonSelected(reason)">
                                            <label class="form-check-label">{{ getReasonLabel(reason) }}</label>
                                        </div>
                                    </div>
                                </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="canShowTeamsCC" class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5 class="mb-0">Notify teams</h5>
                            <p class="text-sm">Select any teams you would like copied in on test result emails</p>
                            <div>
                                <ul class="list-unstyled" style="column-count: 3">
                                <li v-for="(team, index) in myOrganisationSettings.ccEmails" :key="index">
                                    <div class="custom-tick">
                                        <div class="form-check mt-0 mb-3">
                                            <input @change="toggleTeamSelected(team)" class="form-check-input" type="checkbox" :checked="isTeamSelected(team)">
                                            <label class="form-check-label">{{ index }}</label>
                                        </div>
                                    </div>
                                </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5>Select a facility<span class="text-danger">*</span></h5>

                            <div class="row mt-4">
                                <div class="col-lg-3">
                                    <select class="custom-select" :class="{'border border-danger' : errors && errors.facilityId}" v-model="facilityIdComputed">
                                        <!--<option disabled value="null">Please select an option</option>-->
                                        <option v-for="(facility, index) in myFacilities" :key="index" :value="facility.id">{{ facility.name }}</option>
                                    </select>
                                </div>
                            </div>

                            <small v-if="errors && errors.facilityId" class="text-danger mt-1">
                                {{ errors.facilityId[0] }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5>Select a practitioner<span class="text-danger">*</span></h5>

                            <div class="row mt-4">
                                <div class="col-lg-3">
                                    <div class="custom-radio">
                                        <div class="form-check">
                                            <input class="form-check-input" type="radio" name="practitionerRadios" value="0" v-model="orderingOnBehalfOfOtherHcpComputed">
                                            <label class="form-check-label">
                                                {{ formatTitle(myTitle) != null ? `${formatTitle(myTitle)} ${myName}` : myNane }}
                                            </label>
                                        </div>
                                    </div>
                                    <div class="custom-radio">
                                        <div class="form-check">
                                            <input class="form-check-input" type="radio" name="practitionerRadios" value="1" v-model="orderingOnBehalfOfOtherHcpComputed">
                                            <label class="form-check-label">
                                                On behalf of another practitioner 
                                            </label>
                                        </div>
                                    </div>

                                    <div v-if="orderingOnBehalfOfOtherHcpComputed == 1 && orderingOnBehalfOfOtherHcpData.name == null" class="mt-4">
                                        <input ref="practitionerInput" class="form-control" placeholder="Search for practitioner" v-model="practitionerQuery" @input="getPractitioners()" />
                                        <p v-if="!practitioners.length" class="text-danger font-italic text-xs">Please enter at least 4 characters</p>
                                        <div v-if="practitioners.length">
                                            <ul class="list-group m-0 rounded-0">
                                                <li v-for="practitioner in practitioners" :key="practitioner.id" :value="practitioner.id" class="list-group-item list-group-item-action cursor-pointer" @click="setOtherHcpData(practitioner)">{{ practitioner.name }}</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div v-if="orderingOnBehalfOfOtherHcpData.name != null" class="mt-3">
                                        <div class="alert alert-info mb-0">
                                            <div>You have chosen to create this test on behalf of:</div>
                                            <div><strong>{{ orderingOnBehalfOfOtherHcpData.name }}</strong></div>
                                            <div class="cursor-pointer text-right text-sm mt-4" @click="setOrderingOnBehalfOfOtherHcpData({})">Change practitioner?</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <small v-if="errors && errors.practitioner" class="text-danger mt-1">
                                {{ errors.practitioner[0] }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col">
                    <div class="card">
                        <div class="card-body">
                            <h5>Leaflet and Communications Language<span class="text-danger">*</span> <small class="font-italic">(Defaults to patient preferred language)</small></h5>

                            <div class="row mt-4">
                                <div class="col-lg-3">
                                    <select class="custom-select" :class="{'border border-danger' : errors && errors.language}" v-model="languageComputed">
                                        <option disabled selected value="null">Please select a language</option>
                                        <option v-for="language in languages" :value="language.code" :key="language.id">{{language.name}}</option>
                                    </select>
                                </div>
                            </div>

                            <small v-if="errors && errors.language" class="text-danger mt-1">
                                {{ errors.language[0] }}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-4">
                <div class="col">
                    <div class="card pb-25">
                        <div class="card-body">
                            <h5 class="mb-0">Notes</h5>
        
                            <div class="text-right mt-4">
                                <textarea class="form-control note" rows="4" placeholder="Add a note to the test" v-model="noteComputed"></textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="fixed-bottom bg-white py-25">
            <div class="container-fluid">
                <div class="row">
                    <div class="col text-right center-mobile">
                        <router-link :to="{name: 'SelectAPatient'}" class="btn btn-cancel px-5 btn-lg lg-shadow">Back</router-link>
                        <button @click="next()" :disabled="loading" class="btn btn-success btn-lg px-6 lg-shadow ml-2">Next</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Loader from '../../../Common/components/Loader.vue'
import axios from 'axios'
import {mapGetters, mapActions} from 'vuex'
import Breadcrumbs from '@/partials/site-structure/BreadcrumbsComponent.vue'
import CancelTestModalComponent from '@/components/modals/CancelTestModalComponent.vue'
import PatientDatePicker from '@/components/PatientDatePickerComponent.vue'
import TestIssueReason from '../../../Common/enums/TestIssueReason'
import IssueMethod from '../../../Common/enums/IssueMethod'
import SignalRMixin from '@/mixins/SignalRMixin'
import FormatterMixin from '../../../Common/mixins/FormatterMixin'
import ConnectionDiagnosticsModal from '@/components/modals/ConnectionDiagnosticsModalComponent'

export default {
    name: 'TestDetails',

    mixins: [
        SignalRMixin,
        FormatterMixin
    ],

    components: {
        Loader,
        Breadcrumbs,
        PatientDatePicker
    },

    data() {
        const today = this.getDateTimeStartOfToday()
        const eddMinDate = new Date(today)
        const eddMaxDate = new Date(today)
        const testMaxDate = new Date(today)
        const testMinDate = new Date(today)
        eddMaxDate.setFullYear(eddMaxDate.getFullYear() + 1)
        testMaxDate.setFullYear(testMaxDate.getFullYear() + 1)
        
        return {
            loading: false,
            errors: {},
            CancelTestModalComponent,
            TestIssueReason,
            IssueMethod,
            eddMin: eddMinDate,
            eddMax: eddMaxDate,
            testMin: testMinDate,
            testMax: testMaxDate,
            practitionerQuery: '',
            practitionerQueryLoading: false,
            practitioners: []
        }
    },

    async mounted() {
        if(!this.patientName && !this.patientId && !this.patientTitle) {
            return this.$router.push({name: 'Dashboard'})
        }

        if(this.method == IssueMethod.IN_PERSON) {
            this.testMin = this.getDateTimeStartOfToday()
        }
        else {
            // test date add two working days
            const now = new Date()
            const today = this.getDateTimeStartOfToday()
            const todayDayOfWeek = now.getDay()
            const testMinDate = new Date(today)

            var daysToAdd
            if(todayDayOfWeek == 0 || todayDayOfWeek == 1 || todayDayOfWeek == 2 || todayDayOfWeek == 3) daysToAdd = 3
            if(todayDayOfWeek == 4 || todayDayOfWeek == 5) daysToAdd = 5
            if(todayDayOfWeek == 6) daysToAdd = 4

            while(daysToAdd > 0) {
                testMinDate.setDate(testMinDate.getDate() + 1)
                daysToAdd--
            }
            this.testMin = testMinDate
        }

        if(this.myFacilities) {
            this.facilityIdComputed = this.myFacilities[0].id
        }
    },

    computed: {
        ...mapGetters({
            myName: 'UserStore/GetUserFullName',
            myTitle: 'UserStore/GetUserTitle',
            organisationSettingsMap: 'OrganisationStore/GetOrganisationSettingsMap',
            myOrganisationSettings: 'OrganisationStore/GetMyOrganisationSettings',
            organisationId: 'UserStore/GetUserOrganisationId',
            isTrial: 'OrganisationStore/GetIsTrial',
            myFacilities: 'FacilityStore/GetMyEnabledFacilities',
            facilityId: 'TestStore/GetFacilityId',
            patientName: 'TestStore/GetPatientName',
            patientId: 'TestStore/GetPatientId',
            patientTitle: 'TestStore/GetPatientTitle',
            eddDate: 'TestStore/GetEddDate',
            testDueDate: 'TestStore/GetDueDate',
            reason: 'TestStore/GetReason',
            method: 'TestStore/GetMethod',
            note: 'TestStore/GetNote',
            language: 'TestStore/GetLanguage',
            languages: 'LanguageStore/GetLanguages',
            testDatesResponse: 'TestStore/GetTestDatesResponse',
            testReasons: 'TestStore/GetTestReasons',
            testTeamsCCEmails: 'TestStore/GetTestTeamsCCEmails',
            practitionerId: 'UserStore/GetUserPractitionerId',
            orderingOnBehalfOfOtherHcp: 'TestStore/GetOrderingOnBehalfOfOtherHcp',
            orderingOnBehalfOfOtherHcpData: 'TestStore/GetOrderingOnBehalfOfOtherHcpData'
        }),

        canShowRiskFactors: function() {
            var result = true;
            if(!this.myOrganisationSettings) return false
            if(!this.myOrganisationSettings.testReasons) return false
            if(this.reason == null) return false

            if(this.reason === this.TestIssueReason.GD && !this.myOrganisationSettings.testReasons.gdReasons.length) return false
            if(this.reason === this.TestIssueReason.CF && !this.myOrganisationSettings.testReasons.cfReasons.length) return false
            if(this.reason === this.TestIssueReason.TYPE1 && !this.myOrganisationSettings.testReasons.t1Reasons.length) return false
            if(this.reason === this.TestIssueReason.RESEARCH_STUDY && !this.myOrganisationSettings.testReasons.researchStudyReasons.length) return false
            // Settings saved before Type 2 existed have no t2Reasons list
            if(this.reason === this.TestIssueReason.TYPE2 && !(this.myOrganisationSettings.testReasons.t2Reasons || []).length) return false

            return result;
        },

        canShowTeamsCC: function() {
            var result = true;
            if(!this.myOrganisationSettings) return false
            if(!this.myOrganisationSettings.ccEmails) return false
            if(Object.keys(this.myOrganisationSettings.ccEmails).length === 0) return false

            return result;
        },

        orderingOnBehalfOfOtherHcpComputed: {
            get: function() {
                return this.orderingOnBehalfOfOtherHcp
            },

            set: function(val) {
                this.practitioners = []
                this.practitionerQuery = ''
                this.setOrderingOnBehalfOfOtherHcpData({})
                this.setOrderingOnBehalfOfOtherHcp(val)
            }
        },

        languageComputed: {
            get: function() {
                return this.language
            },

            set: function(val) {
                this.setLanguage(val)
            }
        },      

        facilityIdComputed: {
            get: function() {
                return this.facilityId
            }, 

            set: function(val) {
                this.setFacilityId(val)
            }
        },

        eddDateComputed: {
            get: function() {
                return this.eddDate
            }, 

            set: function(val) {
                this.setEddDate(val)
            }
        },

        testDateComputed: {
            get: function() {
                return this.testDueDate
            }, 

            set: function(val) {
                this.setTestDueDate(val)
            }
        },

        reasonComputed: {
            get: function() {
                return this.reason
            }, 

            set: function(val) {
                this.setReason(val)
            }
        },

        noteComputed: {
            get: function() {
                return this.note
            }, 

            set: function(val) {
                this.setNote(val)
            }
        }
    },

    methods: {
        ...mapActions({
            setNotification: 'NotificationStore/SetNotification',
            setDeviceIdInTestStoreAction: 'TestStore/SetDeviceId',
            setFacilityId: 'TestStore/SetFacilityId',
            setEddDate: 'TestStore/SetEddDate',
            setTestDatesResponse: 'TestStore/SetTestDatesResponse',
            setTestDueDate: 'TestStore/SetDueDate',
            setReason: 'TestStore/SetReason',
            setNote: 'TestStore/SetNote',
            setLanguage: 'TestStore/SetLanguage',
            setTestReason: 'TestStore/SetTestReason',
            clearTestReasons: 'TestStore/ClearTestReasons',
            setTestTeamCCEmail: 'TestStore/SetTestTeamCCEmail',
            setOrderingOnBehalfOfOtherHcp: 'TestStore/SetOrderingOnBehalfOfOtherHcp',
            setOrderingOnBehalfOfOtherHcpData: 'TestStore/SetOrderingOnBehalfOfOtherHcpData',
            setDeliveryMethodInTestStoreAction: 'TestStore/SetDeliveryMethod',
            setShippingAddressTypeInTestStoreAction: 'TestStore/SetShippingAddressType',
        }),

        getReasonLabel(reason) {
            var found = this.organisationSettingsMap.find(x => x.key == reason)
            if(found == null) return reason
            return found.label
        },

        toggleTestReason(reason) {
            if(reason == null ) return
            this.setTestReason(reason)
        },

        toggleTeamSelected(team) {
            if(team == null ) return
            this.setTestTeamCCEmail(team)
        },

        isTestReasonSelected(reason) {
            if(reason == null ) return
            return this.testReasons.includes(reason)
        },

        isTeamSelected(teamEmail) {
            if(teamEmail == null ) return
            return this.testTeamsCCEmails.includes(teamEmail)
        },

        reasonChanged() {
            this.setEddDate(null)
            this.setTestDueDate(null)
            this.setTestDatesResponse([])
            this.clearTestReasons()
        },

        async getPractitioners() {
            if(this.practitionerQuery.length <= 3) {
                this.practitioners = []
                return
            }

            this.practitionerQueryLoading = true
            try {
                const response = await axios.get(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/practitioners/lookup/${this.organisationId}/${this.practitionerQuery}`)
                this.practitioners = response.data
            } catch {
                this.practitionerQueryLoading = false
                this.setNotification({
                    type: 'error',
                    title: 'Error',
                    message: 'Sorry, something went wrong while fetching practitioners. Please try again.'
                })
            } finally {
                this.practitionerQueryLoading = false
                this.$refs.practitionerInput.focus()
            }
        },

        setOtherHcpData(practitioner) {
            this.setOrderingOnBehalfOfOtherHcpData(practitioner)
            this.practitioners = []
            this.practitionerQuery = ''
        },

        async getPatientTestDates() {
            this.errors = {}

            if(this.method == this.IssueMethod.POST) {
                this.loading = true
                try {
                    const response = await axios.post(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/dates`, {
                        dueDate: this.eddDate
                    })

                    if(response.data.testDates.length) {
                        response.data.testDates.forEach(data => data.active = false)
                        response.data.testDates.push({
                            name: 'other',
                            date: null,
                            active: false
                        })
                        this.setTestDatesResponse(response.data.testDates)
                    }
                } catch {
                    this.loading = false
                    this.setNotification({
                        type: 'error',
                        title: 'Error',
                        message: 'Sorry, something went wrong while fetching test dates. Please try again.'
                    })
                } finally {
                    this.loading = false
                }
            } 
            if(this.method == this.IssueMethod.IN_PERSON) {
                var testDates = []
                testDates.push({
                    name: 'other',
                    date: this.getDateTimeStartOfToday(),
                    active: true
                })
                this.setTestDatesResponse(testDates)
            }
        },

        async next() {
            this.checkForm()
            if(!Object.keys(this.errors).length) {
                this.loading = true
                
                if(this.isTrial) {
                    await this.setDeviceIdInTestStoreAction(null)
                    await this.setDeliveryMethodInTestStoreAction(0)
                    await this.setShippingAddressTypeInTestStoreAction(null)
                    return this.$router.push({name: 'DeviceNumber'})
                }
                
                await this.setDeviceIdInTestStoreAction(null)

                if(this.method == this.IssueMethod.POST) {
                    return this.$router.push({name: 'ConfirmDeliveryAddress'})
                }

                //if(this.orderingOnBehalfOfOtherHcpComputed == 1) {}

                if(this.method == this.IssueMethod.IN_PERSON) {
                    try {
                        await this.openSignalRConnection()
                        await this.createTokenForAssignment()
                        this.$router.push({name: 'AssignToken'})
                    } catch {
                        this.loading = false
                        this.$modal.show(ConnectionDiagnosticsModal, {}, {width: '600px'})
                        return this.$router.push({name: 'Dashboard'})
                    }
                }
            }
        },

        async createTokenForAssignment() {
            try {
                const response = await axios.post(`${process.env.VUE_APP_AXIOS_DIAGNOSTIC_BASE_URL}/api/v1/tests/createtokenforassignment`, {
                    organisationId: this.organisationId,
                    patientId: this.patientId,
                    practitionerId: this.practitionerId,
                    notificationClientId: this.signalRClientId
                })

                this.setTokenInSignalRStore(response.data.token)
                this.setTokenDateCreatedInSignalRStore(response.data.dateCreated)
                this.setTokenDateExpiryInSignalRStore(response.data.expiryDateTime)
            } catch {
                throw new Error()
            }
        },

        checkForm() {
            this.errors = {}

            if(this.reason != null && this.reason === this.TestIssueReason.GD) {
                if(!this.eddDate) {
                    this.errors.eddDate = ['The expected delivery date field is required']
                }

                const arr = this.testDatesResponse.filter(x => x.active == true)
                if(!arr.length) {
                    this.errors.testDates = ['At least one test date field is required']
                } else {
                    let startOfToday = this.$moment().startOf('day').toISOString()
                    arr.forEach(d => {
                        if(!d.date) {
                            this.errors[`test-date-${d.name}`] = ['The test date field is required']
                        } else {
                            if(!this.$moment(d.date).isSameOrAfter(startOfToday)) {
                                this.errors[`test-date-${d.name}`] = ['This date cannot be in the past']
                            }
                        }
                    })
                }
            }
            
            if(this.reason != null && this.reason !== this.TestIssueReason.GD) {
                if(!this.testDueDate) {
                    this.errors.testDate = ['The test date field is required']
                }

                if(this.testDueDate) {
                    let startOfToday = this.$moment().startOf('day').toISOString()

                    if(!this.$moment(this.testDueDate).isSameOrAfter(startOfToday)) {
                        this.errors.testDate = ['This date cannot be in the past']
                    }
                }
            }

            if(!this.facilityId) {
                this.errors.facilityId = ['The facility is required']
            }

            if(this.reason == null || parseInt(this.reason) < 0) {
                this.errors.reason = ['The reason field is required']
            }

            if(!this.language) {
                this.errors.language = ['The language is required']
            }

            if(this.orderingOnBehalfOfOtherHcp == 1 && (this.orderingOnBehalfOfOtherHcpData.name == null || this.orderingOnBehalfOfOtherHcpData.id == null)) {
                this.errors.practitioner = ['The practitioner is required']
            }

        },

        isDateInPast(date) {
            if(date == null) return false
            return this.$moment() > this.$moment(date).add(1, 'd')
        },

        cancelTest() {
            this.$modal.show(CancelTestModalComponent, {}, {width: '400px'});
        }
    }
}
</script>